<template>
  <NuxtLayout name="main-view" :headline="$t('dbc-change-password')">
    <Card :pt="formCardPassThrough">
      <template #content>
        <FormKit
          :actions="false"
          type="form"
          form-class="rounded-lg"
          @submit="updatePassword"
          #default="{ disabled, state: { valid } }"
          :aria-invalid="errorMessage"
        >
          <DbcBanner
            v-if="errorMessage"
            id="errorBanner"
            :severity="'warn'"
            :message="errorMessage"
          />

          <FormKit
            type="password"
            name="old_password"
            id="old_password"
            :label="$t('dbc-label-old-password')"
            :placeholder="$t('dbc-text-enter-old-password')"
            validation="required"
            prefix-icon="passwordIcon"
            suffix-icon="eyeClosedIcon"
            @suffix-icon-click="(node: FormKitNode ) => togglePasswordIcon(node)"
            :floating-label="true"
            autocomplete="current-password"
          />

          <FormKit
            type="password"
            name="new_password"
            id="new_password"
            :label="$t('dbc-label-new-password')"
            :placeholder="$t('dbc-text-enter-new-password')"
            validation="required|length:8|contains_numeric|contains_symbol|contains_lowercase|contains_uppercase"
            validation-visibility="none"
            prefix-icon="passwordIcon"
            suffix-icon="eyeClosedIcon"
            @suffix-icon-click="(node: FormKitNode ) => togglePasswordIcon(node)"
            :floating-label="true"
            v-model="newPassword"
            help="help"
            autocomplete="new-password"
          >
            <template #help>
              <ul
                id="help-new_password"
                class="mt-xs pl-2xs list-none text-medium"
              >
                <li
                  v-for="(validation, key) in passwordValidations"
                  :key="key"
                  class="flex gap-2xs mb-2xs"
                  :aria-hidden="validation.validate(newPassword)"
                >
                  <DbcSvg
                    :name="
                      validation.validate(newPassword)
                        ? 'dbc-icon-success'
                        : 'dbc-icon-info'
                    "
                  />
                  {{ $t(validation['message-key']) }}
                </li>
              </ul>
            </template>
          </FormKit>

          <FormKit
            type="password"
            name="new_password_confirm"
            id="new_password_confirm"
            :label="$t('dbc-text-confirm-new-password')"
            :placeholder="$t('dbc-text-enter-new-password')"
            validation="required|confirm"
            :validation-messages="{
              confirm: $t('dbc-error-confirm-password'),
            }"
            prefix-icon="passwordIcon"
            suffix-icon="eyeClosedIcon"
            @suffix-icon-click="(node: FormKitNode ) => togglePasswordIcon(node)"
            :floating-label="true"
            autocomplete="new-password"
          />

          <div class="flex flex-col gap-2xs">
            <FormKit
              type="submit"
              id="submit"
              :label="
                !valid
                  ? $t('dbc-text-fill-in-mandatory-fields')
                  : $t('dbc-change-password')
              "
              :disabled="disabled || !valid || isLoading"
              :loading="isLoading"
              :aria-label="
                !valid
                  ? $t('dbc-text-fill-in-mandatory-fields')
                  : !isLoading
                  ? $t('dbc-change-password')
                  : $t('dbc-text-loading')
              "
            />
            <FormKit
              type="button"
              id="abort"
              @click="
                router.push({
                  path: usePagePath('data'),
                })
              "
              :label="$t('dbc-btn-abort')"
            />
          </div>
        </FormKit>
      </template>
    </Card>
  </NuxtLayout>
</template>

<script setup lang="ts">
import type { FormKitNode } from '@formkit/core'
import togglePasswordIcon from '@/formkit/features/togglePasswordIcon'
import passwordValidations from '@/formkit/validations/passwordValidations'

const router = useRouter()

const { t } = useI18n()

const errorMessage = ref('')
const isLoading = ref(false)

const newPassword = ref('')

async function updatePassword(formData: {
  old_password: string
  new_password: string
}) {
  isLoading.value = true
  const response = await fetchUpdatePassword({
    oldPassword: formData.old_password,
    newPassword: formData.new_password,
  })
  if (response.status === 200) {
    router.push({
      path: usePagePath('data'),
      query: { update: 'password' },
    })
  } else if (
    response.data?.errorAttributes &&
    response.data.errorAttributes[0].message
  ) {
    errorMessage.value = response.data.errorAttributes[0].message
  } else if (response.data?.message) {
    errorMessage.value = response.data.message
  } else {
    errorMessage.value = t('dbc-error-server-issues')
  }
  isLoading.value = false
}
</script>
